import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useWaterfallAvailableStore } from "~/components/build/Show/lib/useWaterfallAvailableStore";
import { isTerminalBuildState } from "~/constants/BuildStates";
import NotEnoughData from "~/components/build/Show/components/Waterfall/EmptyStates/NotEnoughData";
import UpgradeRequired from "~/components/build/Show/components/Waterfall/EmptyStates/UpgradeRequired";
import IncompatibleBuild from "~/components/build/Show/components/Waterfall/EmptyStates/IncompatibleBuild";
import TooManySteps from "~/components/build/Show/components/Waterfall/EmptyStates/TooManySteps";
import ViewNotAvailable from "~/components/build/Show/components/Waterfall/EmptyStates/ViewNotAvailable";
import { Build } from "~/components/build/Show/lib/types";
import Skeleton from "~/components/build/Show/components/Waterfall/EmptyStates/Skeleton";
import WaterfallRow from "~/components/build/Show/components/Waterfall/WaterfallRow";

import classNames from "classnames";

import {
  isWaterfallTooLarge,
  waterfallQueryOptions,
  WaterfallRowType,
} from "~/components/build/Show/lib/queries/waterfall";
import { FilterByOption, useFilterStore } from "~/components/build/Show/lib/useFilterStore";
import { JOB_STATE_FILTERS } from "~/components/build/Show/lib/filterConstants";

const EMPTY_FILTERS: FilterByOption[] = [];
const EMPTY_SELECTOR = () => EMPTY_FILTERS;

function MissingData({ build }: { build: Build }) {
  if (isTerminalBuildState(build.state)) {
    return <IncompatibleBuild />;
  }
  return <NotEnoughData />;
}

export default function WaterfallPage({ allowCollapse = false }: { allowCollapse: boolean }) {
  const waterfallAvailable = useWaterfallAvailableStore((state) => state.waterfallAvailable);

  if (Features.DisableStepsWaterfallView) {
    return <ViewNotAvailable />;
  }

  // Need to upgrade to see the waterfall
  if (!waterfallAvailable) {
    return <UpgradeRequired />;
  }

  return (
    <div className="relative min-w-0 flex-auto">
      <div
        className={classNames(
          "flex h-full w-full",
          Features.BuildSidebar ? "absolute overflow-y-auto" : "",
        )}
      >
        <WaterfallView allowCollapse={allowCollapse} />
      </div>
    </div>
  );
}

/** Check if a waterfall row matches any of the provided filters based on job state data. */
const rowMatchesFilters = (row: WaterfallRowType, filters: FilterByOption[]): boolean => {
  const { job_state, job_passed, job_soft_failed } = row;

  // If no job state data, can't filter (shouldn't happen for leaf rows)
  if (!job_state) {
    return false;
  }

  const jobData = { state: job_state, passed: job_passed, softFailed: job_soft_failed };

  return filters.some((filter) => JOB_STATE_FILTERS[filter](jobData));
};

/** Filter the rows based on the active filters applied to job states. */
const filterRows = (data: WaterfallRowType[], filters: FilterByOption[]) => {
  if (filters.length === 0) {
    return data;
  }

  function filterChildren(children: WaterfallRowType[]): WaterfallRowType[] {
    return children.reduce<WaterfallRowType[]>((acc, child) => {
      if (child.children.length) {
        const filteredChildren = filterChildren(child.children);
        if (filteredChildren.length > 0) {
          acc.push({ ...child, children: filteredChildren });
        }
        return acc;
      }

      if (rowMatchesFilters(child, filters)) {
        acc.push(child);
      }

      return acc;
    }, []);
  }

  return filterChildren(data);
};

const WaterfallView = ({ allowCollapse }: { allowCollapse: boolean }) => {
  const { build } = useBuild();

  const tooManySteps = isWaterfallTooLarge(build);

  const { data, isLoading, refetch } = useQuery(waterfallQueryOptions(build));

  // In the classic (non-sidebar) path, LegacyBuildContextProvider updates the
  // build object reference on each BuildShowStore poll, but there's no
  // ActionCable-driven query invalidation. Debounce-refetch the waterfall
  // query when the build changes so it stays fresh during in-progress builds.
  const debouncedRefetch = useMemo(() => debounce(() => refetch(), 1000), [refetch]);

  useEffect(() => {
    if (Features.BuildSidebar || Features.DisableStepsWaterfallView) {
      return;
    }
    debouncedRefetch();

    return () => debouncedRefetch.cancel();
  }, [build, debouncedRefetch]);

  // Only apply filters in the sidebar experience where users have UI controls
  // to manage filters. In the classic view, there's no filter UI on the
  // waterfall tab, so applying the auto-set "Failed" filter silently hides
  // passing jobs with no way for the user to clear it.
  const filters = useFilterStore(
    Features.BuildSidebar ? (state) => state.filterBy : EMPTY_SELECTOR,
  );

  const filteredRows = useMemo(() => {
    if (!data || tooManySteps) {
      return [];
    }
    return filterRows(data.chart_data, filters);
  }, [data, filters, tooManySteps]);

  if (tooManySteps) {
    return <TooManySteps />;
  }

  if (!data && isLoading) {
    return <Skeleton />;
  }

  if (!data) {
    return null;
  }

  if (data.chart_data.length === 0) {
    return <MissingData build={build} />;
  }

  return (
    <div className="flex-1" data-testid="waterfall">
      <section
        className={classNames(
          "relative m-0 flex w-full border-gray-400 contain-layout",
          Features.BuildSidebar ? "border-b" : "rounded-lg border",
        )}
      >
        <div className="bg-silver -m-[1px] w-full p-0">
          {filteredRows.map((row) => {
            const uniqueKey = [row.step_uuid, row.job_uuid];
            return (
              <WaterfallRow
                row={row}
                uniqueKey={uniqueKey}
                key={uniqueKey.join("-")}
                barContainerPadding={data.bar_container_padding}
                depth={0}
                allowCollapse={allowCollapse}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
};
